/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { api } from "core/api";
import { Card, Button, SvgIcon } from "core/components";
import { ProjectFormData, ProjectStatus } from "core/schema";
import { change_order_material_requests_admin_project_path, change_order_purchase_requests_admin_project_path } from "routes";
import ProjectMaterialRequests from "./ProjectMaterialRequests";
import ProjectPurchaseRequests from "./ProjectPurchaseRequests";
import ProjectChangeOrderRequisitionForm from "./ProjectChangeOrderRequisitionForm";

type ProjectChangeOrderRequisitionsProps = {
  project: ProjectFormData;
};

const ProjectChangeOrderRequisitions = ({
  project,
  is_admin_or_architect,
  is_engineer,
  can_approve_reject,
  can_delete
}: ProjectChangeOrderRequisitionsProps): JSX.Element => {
  const [materialRequests, setMaterialRequests] = React.useState([]);
  const [purchaseRequests, setPurchaseRequests] = React.useState([]);
  const [showRequisitions, setShowRequisitions] = React.useState(true);

  const getMaterialRequests = async () => {
    try {
      const response = await api.get(change_order_material_requests_admin_project_path({ id: project.id }));
      return response.data;
    } catch (error) {
      console.error(error);
    }
  };

  const getPurchaseRequests = async () => {
    try {
      const response = await api.get(change_order_purchase_requests_admin_project_path({ id: project.id }));
      return response.data;
    } catch (error) {
      console.error(error);
    }
  };

  const getRequests = React.useCallback(async () => {
    setMaterialRequests(await getMaterialRequests());
    setPurchaseRequests(await getPurchaseRequests());
  }, []);

  React.useEffect(() => {
    if (showRequisitions) {
      getRequests().catch(console.error);
    }
  }, [showRequisitions]);

  const hasRequisitions = () => {
    return materialRequests.length > 0 || purchaseRequests.length > 0;
  };

  const onSubmit = () => {
    setShowRequisitions(!showRequisitions);
  };

  const onMaterialRequestApproved = () => {
    getRequests().catch(console.error);
  };

  const canShowRequisitions = () => {
    return project.status == ProjectStatus.OnGoing;
  };

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row className="mb-8">
          <Col>
            <SvgIcon name="clipboard-list" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No change request requisitions yet.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  return (
    <Card.Body>
      <Container fluid={true}>
        <Row className="mb-2">
          <Col lg={12} className="d-flex justify-content-end">
            {(is_admin_or_architect || is_engineer) && showRequisitions && canShowRequisitions() && (
              <Button
                className="button--brand"
                icon="plus"
                title="New Change Request Requisition"
                type="button"
                onClick={() => {
                  setShowRequisitions(!showRequisitions);
                }}
              />
            )}
          </Col>
        </Row>
        <Row>
          {showRequisitions ? (
            <>
              {hasRequisitions() ? (
                <>
                  <Col>
                    <ProjectMaterialRequests
                      project={project}
                      material_requests={materialRequests}
                      getMaterialRequests={getMaterialRequests}
                      onMaterialRequestApproved={onMaterialRequestApproved}
                      can_approve_reject={can_approve_reject}
                      can_delete={can_delete}
                    />
                  </Col>
                  <Col>
                    <ProjectPurchaseRequests
                      project={project}
                      purchase_requests={purchaseRequests}
                      getPurchaseRequests={getPurchaseRequests}
                      can_approve_reject={can_approve_reject}
                      can_delete={can_delete}
                    />
                  </Col>
                </>
              ) : (
                noContent()
              )}
            </>
          ) : (
            <ProjectChangeOrderRequisitionForm
              project={project}
              onSubmit={onSubmit}
              is_admin_or_architect={is_admin_or_architect}
              is_engineer={is_engineer}
            />
          )}
        </Row>
      </Container>
    </Card.Body>
  );
};

export default ProjectChangeOrderRequisitions;
